/**
 * ComingSoonIntegrationSettings — Skeleton for future integrations
 *
 * Shown when navigating to /settings/integrations/:provider for providers
 * that are not yet implemented (WooCommerce, Priority, HubSpot, etc.)
 * or when there's no active connection for an implemented provider.
 *
 * Route: /app/settings/integrations/:provider (catch-all)
 */

import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Clock, Plug, Sparkles } from 'lucide-react';
import { SettingsTabs } from '@/components/settings';

// Provider display info for known future providers
const PROVIDER_META: Record<string, { label: string; logoText: string; logoBg: string }> = {
  woocommerce:  { label: 'WooCommerce',          logoText: '🛒', logoBg: 'bg-purple-50' },
  magento:      { label: 'Magento / Adobe Commerce', logoText: '🔶', logoBg: 'bg-orange-50' },
  priority_erp: { label: 'Priority ERP',         logoText: '🏢', logoBg: 'bg-blue-50' },
  hubspot:      { label: 'HubSpot CRM',          logoText: '🧡', logoBg: 'bg-amber-50' },
  google_sheets:{ label: 'Google Sheets',        logoText: '📊', logoBg: 'bg-emerald-50' },
};

const PLACEHOLDER_FEATURES: Record<string, string[]> = {
  woocommerce: [
    'סנכרון מוצרים ומלאי',
    'ייבוא הזמנות אוטומטי',
    'עדכון מחירים דו-כיווני',
    'ניהול קטגוריות מוצרים',
  ],
  magento: [
    'סנכרון קטלוג מוצרים',
    'ניהול מחסנים מרובים',
    'ייבוא הזמנות',
    'עדכון מלאי בזמן אמת',
  ],
  priority_erp: [
    'ייבוא/ייצוא נתונים ERP',
    'סנכרון לקוחות וספקים',
    'חשבוניות ומסמכי מכירה',
    'ניהול מחירונים',
  ],
  hubspot: [
    'סנכרון לקוחות ועסקאות',
    'עדכון סטטוס עסקאות',
    'ייצוא נתוני מכירות',
    'חיבור לפייפליין CRM',
  ],
  google_sheets: [
    'ייצוא דוחות מלאי',
    'ייצוא דוחות מכירות',
    'עדכון מחירים מגיליון',
    'תזמון ייצוא אוטומטי',
  ],
};

export function ComingSoonIntegrationSettings() {
  const { provider } = useParams<{ provider: string }>();
  const meta = provider ? PROVIDER_META[provider] : null;
  const features = provider ? (PLACEHOLDER_FEATURES[provider] ?? []) : [];

  const label    = meta?.label    ?? provider ?? 'ממשק';
  const logoText = meta?.logoText ?? '🔌';
  const logoBg   = meta?.logoBg   ?? 'bg-gray-50';

  return (
    <div className="max-w-5xl mx-auto space-y-6" dir="rtl">
      <SettingsTabs />

      {/* Back */}
      <Link
        to="/settings/integrations"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
      >
        <ArrowRight className="w-4 h-4" />
        חזרה לממשקים וחיבורים
      </Link>

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 flex items-center gap-4 shadow-sm">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shadow-sm ${logoBg}`}>
          {logoText}
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">הגדרות {label}</h1>
          <span className="flex items-center gap-1 mt-1 text-xs font-semibold text-amber-700
                           bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full w-fit">
            <Clock className="w-3 h-3" />
            בקרוב
          </span>
        </div>
      </div>

      {/* Coming soon card */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-8 py-12 flex flex-col items-center text-center gap-5">
          <div className="p-4 bg-gray-50 rounded-2xl">
            <Sparkles className="w-8 h-8 text-gray-400" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-bold text-gray-800">הגדרות ממשק {label} בפיתוח</h2>
            <p className="text-sm text-gray-500 max-w-md leading-relaxed">
              עמוד ההגדרות הייעודי עבור ממשק זה יהיה זמין בקרוב.
              כדי לחבר את {label} לחשבונך, לחץ על "חבר עכשיו" בדף הממשקים.
            </p>
          </div>

          <Link
            to="/settings/integrations"
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700
                       text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
          >
            <Plug className="w-4 h-4" />
            חזרה לדף הממשקים
          </Link>
        </div>
      </div>

      {/* Preview of future settings */}
      {features.length > 0 && (
        <div className="bg-white border border-dashed border-gray-200 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-dashed border-gray-200 bg-gray-50/50">
            <p className="text-sm font-semibold text-gray-500">תצוגה מקדימה — הגדרות עתידיות</p>
          </div>
          <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {features.map(f => (
              <div
                key={f}
                className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl opacity-60"
              >
                <div className="w-4 h-4 rounded border-2 border-gray-300 shrink-0" />
                <span className="text-sm text-gray-500">{f}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
